// src/App.tsx
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { useFirebaseAuth } from './hooks/useFirebaseAuth';
import { useThemeStore }   from './stores/useThemeStore';

// ── Layout ──────────────────────────────────────────────────────────────────
import { Navbar }  from './components/layout/Navbar.tsx';
import { Footer }  from './components/layout/Footer.tsx';

// ── Global modals (auth / payment / profile) ─────────────────────────────────
import { AuthModal }          from './components/auth/AuthModal';
import { PaymentModal }       from './components/payment/PaymentModal';
import { ProfileModal }       from './components/auth/ProfileModal';
import { DeleteAccountModal } from './components/auth/DeleteAccountModal';

// ── Legal modals ─────────────────────────────────────────────────────────────
import { PrivacyModal }       from './components/legal/PrivacyModal';
import { CookiesModal }       from './components/legal/CookiesModal';
import { TermsModal }         from './components/legal/TermsModal';
import { AccessibilityModal } from './components/legal/AccessibilityModal';

// ── Cookie consent banner ─────────────────────────────────────────────────────
import { CookieConsentBanner } from './components/layout/CookieConsentBanner';

// ── Pages ─────────────────────────────────────────────────────────────────────
import HomePage       from './pages/HomePage.tsx';
import ExercisePage   from './pages/ExercisePage.tsx';
import TestPage       from './pages/TestPage';
import PaymentSuccess from './pages/PaymentSuccess.tsx';
import PaymentCancel  from './pages/PaymentCancel.tsx';
import AboutPage      from './pages/AboutPage.tsx';
import PartnersPage   from './pages/PartnersPage.tsx';
import ContactPage    from './pages/ContactPage.tsx';
import AdminPage      from './pages/AdminPage.tsx';

export default function App() {
  useFirebaseAuth();
  const theme  = useThemeStore((s) => s.theme);
  const isDark = theme === 'dark';

  return (
    <BrowserRouter>
      <div
        style={{
          minHeight:       '100vh',
          backgroundColor: isDark ? '#080c14' : '#f8fafc',
          color:           isDark ? '#f1f5f9' : '#1e293b',
          fontFamily:      'system-ui, -apple-system, "Segoe UI", sans-serif',
          display:         'flex',
          flexDirection:   'column',
          transition:      'background-color 0.25s ease, color 0.25s ease',
        }}
      >
        {/* ── Top navigation ───────────────────────────────────────────── */}
        <Navbar />

        {/* ── Page content ─────────────────────────────────────────────── */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route path="/"                element={<HomePage />} />
            <Route path="/exercise"        element={<ExercisePage />} />
            <Route path="/test"            element={<TestPage />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel"  element={<PaymentCancel />} />
            <Route path="/admin"           element={<AdminPage />} />
            <Route path="/about"           element={<AboutPage />} />
            <Route path="/partners"        element={<PartnersPage />} />
            <Route path="/contact"         element={<ContactPage />} />
            <Route path="*"               element={<HomePage />} />
          </Routes>
        </main>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <Footer />

        {/* ── Global modal layer ───────────────────────────────────────── */}
        {/* Auth / payment */}
        <AuthModal />
        <PaymentModal />
        <ProfileModal />
        <DeleteAccountModal />

        {/* Legal modals — mounted globally so they render above everything */}
        <PrivacyModal />
        <CookiesModal />
        <TermsModal />
        <AccessibilityModal />

        {/* Cookie consent — fixed position, renders above all page content */}
        <CookieConsentBanner />
      </div>
    </BrowserRouter>
  );
}